import { useEffect, useRef } from 'react';
import type { HubConnection } from '@microsoft/signalr';
import { connectOrderHub, type OrderStatusChangedPayload } from '../api/common/OrderHub';
import { tokenStore } from '../api/tokenStore';

export type { OrderStatusChangedPayload };

/**
 * Subscribes to the `OnOrderStatusChanged` event pushed by OrderService's OrderHub (via
 * `/hubs/orders`) — delivered to the owning customer and every connected manager/admin.
 * Connects on mount, disconnects on unmount. Uses the minimal direct connectOrderHub client
 * (same proven pattern as connectInventoryHub/connectMessagesHub), not
 * ChatHubService/SignalRServiceBase — see MessagesHub.ts's doc comment for why that base
 * class is untested boilerplate best avoided for new work.
 */
export function useOrderTracking(onStatusChanged?: (payload: OrderStatusChangedPayload) => void) {
  const onStatusChangedRef = useRef(onStatusChanged);
  onStatusChangedRef.current = onStatusChanged;

  const connectionRef = useRef<HubConnection | null>(null);

  useEffect(() => {
    const token = tokenStore.get()?.accessToken;
    if (!token) return;

    const connection = connectOrderHub(token, (payload) => onStatusChangedRef.current?.(payload));
    connectionRef.current = connection;

    return () => {
      connection.stop().catch(() => {
        /* best-effort — the tab is navigating away or unmounting either way */
      });
      connectionRef.current = null;
    };
  }, []);
}
